import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { UserCog, Plus, Edit, Trash2 } from 'lucide-react';
import { StaffCalendarLink } from './StaffCalendarLink';

const AVATAR_COLORS = ['#8B5CF6', '#06B6D4', '#EC4899', '#F59E0B', '#10B981', '#6366F1', '#EF4444', '#3B82F6'];

const StaffTab = ({ staffList, services, onRefresh }) => {
  const { API, getAuthHeaders } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', role: 'operator', services: [], is_active: true,
    avatar_color: AVATAR_COLORS[0]
  });
  const [calendarLinks, setCalendarLinks] = useState([]);

  const loadCalendarLinks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff/calendar/links`, getAuthHeaders());
      setCalendarLinks(res.data);
    } catch {
      setCalendarLinks([]);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { loadCalendarLinks(); }, [loadCalendarLinks]);

  const openDialog = (member = null) => {
    if (member) {
      setEditingStaff(member);
      setForm({
        name: member.name, email: member.email || '', phone: member.phone || '',
        role: member.role, services: member.services || [], is_active: member.is_active,
        avatar_color: member.avatar_color || AVATAR_COLORS[0]
      });
    } else {
      setEditingStaff(null);
      setForm({
        name: '', email: '', phone: '', role: 'operator', services: [], is_active: true,
        avatar_color: AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)]
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        await axios.put(`${API}/staff/${editingStaff.id}`, form, getAuthHeaders());
      } else {
        await axios.post(`${API}/staff`, form, getAuthHeaders());
      }
      onRefresh();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Errore nel salvataggio staff:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo operatore?')) return;
    try {
      await axios.delete(`${API}/staff/${id}`, getAuthHeaders());
      onRefresh();
    } catch (error) {
      console.error('Errore nell\'eliminazione staff:', error);
    }
  };

  const toggleService = (serviceId) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="font-outfit">Staff / Operatori</CardTitle>
          <CardDescription>Gestisci il personale e i servizi che possono eseguire</CardDescription>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()} className="w-full sm:w-auto min-h-[44px]" data-testid="add-staff-btn">
              <Plus className="w-4 h-4 mr-2" />Aggiungi Operatore
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-outfit">{editingStaff ? 'Modifica Operatore' : 'Nuovo Operatore'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="staff-name">Nome Completo *</Label>
                <Input id="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Maria Rossi" required data-testid="staff-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="staff-email">Email</Label>
                  <Input id="staff-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@esempio.it" data-testid="staff-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="staff-phone">Telefono</Label>
                  <Input id="staff-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 333..." data-testid="staff-phone-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-role">Ruolo</Label>
                <Select value={form.role} onValueChange={(value) => setForm({ ...form, role: value })}>
                  <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="junior">Junior</SelectItem>
                    <SelectItem value="operator">Operatore</SelectItem>
                    <SelectItem value="senior">Senior</SelectItem>
                    <SelectItem value="specialist">Specialista</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Colore Avatar</Label>
                <div className="flex gap-2 flex-wrap">
                  {AVATAR_COLORS.map((color) => (
                    <button key={color} type="button"
                      className={`w-8 h-8 rounded-full transition-all ${form.avatar_color === color ? 'ring-2 ring-offset-2 ring-primary' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setForm({ ...form, avatar_color: color })}
                      data-testid={`avatar-color-${color}`}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Servizi Abilitati</Label>
                <p className="text-xs text-muted-foreground mb-2">Seleziona i servizi che questo operatore può eseguire</p>
                {services.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                    {services.filter(s => s.is_active).map((service) => (
                      <div key={service.id} className="flex items-center space-x-3">
                        <Checkbox id={`service-check-${service.id}`} checked={form.services.includes(service.id)} onCheckedChange={() => toggleService(service.id)} data-testid={`staff-service-${service.id}`} />
                        <label htmlFor={`service-check-${service.id}`} className="text-sm font-medium leading-none cursor-pointer">{service.name}</label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground p-3 border rounded-lg text-center">Aggiungi prima dei servizi dalla tab "Servizi"</p>
                )}
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="staff-active">Operatore Attivo</Label>
                <Switch id="staff-active" checked={form.is_active} onCheckedChange={(checked) => setForm({ ...form, is_active: checked })} data-testid="staff-active-switch" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Annulla</Button>
                <Button type="submit" data-testid="save-staff-btn">{editingStaff ? 'Aggiorna' : 'Salva'} Operatore</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <p className="mb-4 rounded-lg border border-dashed px-3 py-2 text-xs text-muted-foreground" data-testid="staff-calendar-hint">
          Facoltativo: ogni operatore può collegare il proprio Google Calendar. Gli appuntamenti compaiono sul suo
          telefono e i suoi impegni personali bloccano le prenotazioni online. Senza collegamento l’agenda funziona come sempre.
        </p>
        {staffList.length > 0 ? (
          <div className="space-y-3">
            {staffList.map((member) => (
              <div key={member.id} className={`grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-start gap-x-3 gap-y-2 p-3 sm:p-4 rounded-lg border ${member.is_active ? 'border-border' : 'border-muted bg-muted/30'}`} data-testid={`staff-item-${member.id}`}>
                <Avatar className="w-12 h-12 shrink-0">
                  <AvatarFallback style={{ backgroundColor: member.avatar_color || '#6366F1' }} className="text-white text-lg">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium break-words">{member.name}</p>
                    {!member.is_active && <Badge variant="secondary">Disattivato</Badge>}
                    <Badge variant="outline" className="capitalize">{member.role}</Badge>
                  </div>
                  {(member.email || member.phone) && <p className="text-sm text-muted-foreground break-all mt-0.5">{member.email || member.phone}</p>}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {member.services?.map(serviceId => {
                      const service = services.find(s => s.id === serviceId);
                      return service ? <Badge key={serviceId} variant="secondary" className="text-xs">{service.name}</Badge> : null;
                    })}
                    {(!member.services || member.services.length === 0) && <span className="text-xs text-muted-foreground">Nessun servizio assegnato</span>}
                  </div>
                  <StaffCalendarLink
                    member={member}
                    link={calendarLinks.find(l => l.staff_id === member.id)}
                    onChanged={loadCalendarLinks}
                  />
                </div>
                <div className="col-span-2 sm:col-span-1 flex items-center justify-end gap-2 border-t sm:border-t-0 pt-2 sm:pt-0">
                  <Button variant="outline" size="sm" className="min-h-[40px] sm:min-h-0 sm:w-9 sm:h-9 sm:p-0" onClick={() => openDialog(member)} data-testid={`edit-staff-${member.id}`}>
                    <Edit className="w-4 h-4 mr-2 sm:mr-0" /><span className="sm:hidden">Modifica</span>
                  </Button>
                  <Button variant="outline" size="sm" className="min-h-[40px] sm:min-h-0 sm:w-9 sm:h-9 sm:p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(member.id)} data-testid={`delete-staff-${member.id}`}>
                    <Trash2 className="w-4 h-4 mr-2 sm:mr-0" /><span className="sm:hidden">Elimina</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <UserCog className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Nessun operatore configurato</p>
            <p className="text-sm text-muted-foreground mt-1">Aggiungi operatori per poter creare appuntamenti</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StaffTab;
